import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api_clients';
import { useOrganizationStore } from '../stores/organization-store';
import type {
  ConnectRelayRequest,
  AssignRelayTransactionRequest,
  SyncRelayRequest,
  UpdateRelayConfigRequest,
  ManualMatchRelayDriverRequest,
  ResponsesRelayStatusResponseData,
  ResponsesRelaySyncResponseData,
  ResponsesRelayTransaction,
  ResponsesRelayTransactionListResponseData,
  ResponsesRelayTransactionResponseData,
  ResponsesRelayConfigResponseData,
  ResponsesRelayPoliciesResponseData,
  ResponsesRelayUnmatchedDriversResponseData,
  ResponsesRelayDriverMatchResponseData,
  ResponsesRelayManualMatchResponseData,
} from '../api_clients/api/api';
import { useRBAC } from '../../hooks/use-rbac';

export type { ResponsesRelayStatusResponseData as RelayStatus };
export type { ResponsesRelayTransaction };
export type { ResponsesRelaySyncResponseData as RelaySyncResult };
export type { ResponsesRelayTransactionListResponseData as RelayTransactionList };
export type { ResponsesRelayTransactionResponseData as RelayTransactionDetail };
export type { ResponsesRelayConfigResponseData as RelayConfig };
export type { ResponsesRelayPoliciesResponseData as RelayPolicies };
export type { ResponsesRelayUnmatchedDriversResponseData as RelayUnmatchedDrivers };
export type { ResponsesRelayDriverMatchResponseData as RelayDriverMatchResult };
export type { ResponsesRelayManualMatchResponseData as RelayManualMatchResult };

export interface RelayTransactionFilters {
  page?: number;
  limit?: number;
  dateFrom?: string | null;
  dateTo?: string | null;
  driverId?: string | null;
  truckId?: string | null;
  transactionType?: string | null;
  status?: string | null;
  state?: string | null;
}

export const relayKeys = {
  all: ['relay'] as const,
  status: (orgId: string) => [...relayKeys.all, 'status', orgId] as const,
  config: (orgId: string) => [...relayKeys.all, 'config', orgId] as const,
  policies: (orgId: string) => [...relayKeys.all, 'policies', orgId] as const,
  unmatchedDrivers: (orgId: string) => [...relayKeys.all, 'unmatched-drivers', orgId] as const,
  transactions: (orgId: string, filters: RelayTransactionFilters) =>
    [...relayKeys.all, 'transactions', orgId, filters] as const,
  transaction: (orgId: string, id: string) =>
    [...relayKeys.all, 'transaction', orgId, id] as const,
};

export function useRelayStatus() {
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useQuery({
    queryKey: relayKeys.status(orgUnid),
    queryFn: async (): Promise<ResponsesRelayStatusResponseData | null> => {
      if (!orgUnid) return null;
      const response = await apiClient.relayApi.getRelayStatus(orgUnid, '');
      return response.data?.data || null;
    },
    enabled: !!orgUnid && isAdminOrOwner,
  });
}

export function useConnectRelay() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (data: { apiKey: string; accountId: string }) => {
      if (!orgUnid) throw new Error('Organization not found');
      const request: ConnectRelayRequest = {
        apiKey: data.apiKey,
        accountId: data.accountId,
      };
      const response = await apiClient.relayApi.connectRelay(orgUnid, '', request);
      return response.data?.data;
    },
    onSuccess: () => {
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: relayKeys.status(orgUnid) });
      }
      queryClient.invalidateQueries({ queryKey: ['settings', 'integrations'] });
    },
  });
}

export function useDisconnectRelay() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async () => {
      if (!orgUnid) throw new Error('Organization not found');
      const response = await apiClient.relayApi.disconnectRelay(orgUnid, '');
      return response.data?.data;
    },
    onSuccess: () => {
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: relayKeys.status(orgUnid) });
      }
      queryClient.invalidateQueries({ queryKey: ['settings', 'integrations'] });
    },
  });
}

export function useRelaySync() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (data: { dateFrom: string; dateTo: string }): Promise<ResponsesRelaySyncResponseData | null | undefined> => {
      if (!orgUnid) throw new Error('Organization not found');
      const request: SyncRelayRequest = {
        dateFrom: data.dateFrom,
        dateTo: data.dateTo,
      };
      const response = await apiClient.relayApi.syncRelay(orgUnid, '', request);
      return response.data?.data;
    },
    onSuccess: () => {
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: relayKeys.status(orgUnid) });
        queryClient.invalidateQueries({ queryKey: [...relayKeys.all, 'transactions', orgUnid] });
      }
    },
  });
}

export function useRelayTransactions(filters: RelayTransactionFilters = {}) {
  const { organization } = useOrganizationStore();
  const orgUnid = organization?.id || '';

  return useQuery({
    queryKey: relayKeys.transactions(orgUnid, filters),
    queryFn: async (): Promise<ResponsesRelayTransactionListResponseData | null> => {
      if (!orgUnid) return null;
      const response = await apiClient.relayApi.listRelayTransactions(
        orgUnid,
        '',
        filters.dateFrom,
        filters.dateTo,
        filters.driverId,
        filters.limit ?? 25,
        filters.page ?? 1,
        filters.state,
        filters.status,
        filters.transactionType,
        filters.truckId
      );
      return response.data?.data || null;
    },
    enabled: !!orgUnid,
  });
}

export function useRelayTransaction(transactionUnid: string) {
  const { organization } = useOrganizationStore();
  const orgUnid = organization?.id || '';

  return useQuery({
    queryKey: relayKeys.transaction(orgUnid, transactionUnid),
    queryFn: async (): Promise<ResponsesRelayTransaction | null> => {
      if (!orgUnid || !transactionUnid) return null;
      const response = await apiClient.relayApi.getRelayTransaction(orgUnid, transactionUnid, '');
      return response.data?.data?.transaction || null;
    },
    enabled: !!orgUnid && !!transactionUnid,
  });
}

export function useRelayRematch() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (transactionUnid: string): Promise<ResponsesRelayTransaction | null | undefined> => {
      if (!orgUnid) throw new Error('Organization not found');
      const response = await apiClient.relayApi.rematchRelayTransaction(
        orgUnid,
        transactionUnid,
        '',
        {}
      );
      return response.data?.data?.transaction;
    },
    onSuccess: (_data, transactionUnid) => {
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: relayKeys.transaction(orgUnid, transactionUnid) });
        queryClient.invalidateQueries({ queryKey: [...relayKeys.all, 'transactions', orgUnid] });
        queryClient.invalidateQueries({ queryKey: relayKeys.status(orgUnid) });
      }
    },
  });
}

export function useRelayAssign() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (params: {
      transactionUnid: string;
      driverId?: string | null;
      truckId?: string | null;
      loadId?: string | null;
    }): Promise<ResponsesRelayTransaction | null | undefined> => {
      if (!orgUnid) throw new Error('Organization not found');
      const request: AssignRelayTransactionRequest = {
        driverId: params.driverId,
        truckId: params.truckId,
        loadId: params.loadId,
      };
      const response = await apiClient.relayApi.assignRelayTransaction(
        orgUnid,
        params.transactionUnid,
        '',
        request
      );
      return response.data?.data?.transaction;
    },
    onSuccess: (_data, params) => {
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: relayKeys.transaction(orgUnid, params.transactionUnid) });
        queryClient.invalidateQueries({ queryKey: [...relayKeys.all, 'transactions', orgUnid] });
        queryClient.invalidateQueries({ queryKey: relayKeys.status(orgUnid) });
      }
    },
  });
}

export function useRelayConfig() {
  const { organization } = useOrganizationStore();
  const orgUnid = organization?.id || '';

  return useQuery({
    queryKey: relayKeys.config(orgUnid),
    queryFn: async (): Promise<ResponsesRelayConfigResponseData | null> => {
      if (!orgUnid) return null;
      const response = await apiClient.relayApi.getRelayConfig(orgUnid, '');
      return response.data?.data || null;
    },
    enabled: !!orgUnid,
  });
}

export function useUpdateRelayConfig() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (data: { policyIds?: string[]; syncAllPolicies?: boolean }): Promise<ResponsesRelayConfigResponseData | null | undefined> => {
      if (!orgUnid) throw new Error('Organization not found');
      const request: UpdateRelayConfigRequest = {
        policyIds: data.policyIds,
        syncAllPolicies: data.syncAllPolicies,
      };
      const response = await apiClient.relayApi.updateRelayConfig(orgUnid, '', request);
      return response.data?.data;
    },
    onSuccess: () => {
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: relayKeys.config(orgUnid) });
        queryClient.invalidateQueries({ queryKey: relayKeys.status(orgUnid) });
      }
    },
  });
}

export function useRelayPolicies() {
  const { organization } = useOrganizationStore();
  const orgUnid = organization?.id || '';

  return useQuery({
    queryKey: relayKeys.policies(orgUnid),
    queryFn: async (): Promise<ResponsesRelayPoliciesResponseData | null> => {
      if (!orgUnid) return null;
      const response = await apiClient.relayApi.getRelayPolicies(orgUnid, '');
      return response.data?.data || null;
    },
    enabled: !!orgUnid,
  });
}

export function useUnmatchedRelayDrivers() {
  const { organization } = useOrganizationStore();
  const orgUnid = organization?.id || '';

  return useQuery({
    queryKey: relayKeys.unmatchedDrivers(orgUnid),
    queryFn: async (): Promise<ResponsesRelayUnmatchedDriversResponseData | null> => {
      if (!orgUnid) return null;
      const response = await apiClient.relayApi.listUnmatchedRelayDrivers(orgUnid, '');
      return response.data?.data || null;
    },
    enabled: !!orgUnid,
  });
}

export function useMatchRelayDrivers() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (): Promise<ResponsesRelayDriverMatchResponseData | null | undefined> => {
      if (!orgUnid) throw new Error('Organization not found');
      const response = await apiClient.relayApi.matchRelayDrivers(orgUnid, '', {});
      return response.data?.data;
    },
    onSuccess: () => {
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: relayKeys.unmatchedDrivers(orgUnid) });
        queryClient.invalidateQueries({ queryKey: relayKeys.status(orgUnid) });
      }
    },
  });
}

export function useManualMatchRelayDriver() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (data: { driverUnid: string; relayDriverId: string }): Promise<ResponsesRelayManualMatchResponseData | null | undefined> => {
      if (!orgUnid) throw new Error('Organization not found');
      const request: ManualMatchRelayDriverRequest = {
        driverUnid: data.driverUnid,
        relayDriverId: data.relayDriverId,
      };
      const response = await apiClient.relayApi.manualMatchRelayDriver(orgUnid, '', request);
      return response.data?.data;
    },
    onSuccess: () => {
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: relayKeys.unmatchedDrivers(orgUnid) });
        queryClient.invalidateQueries({ queryKey: relayKeys.status(orgUnid) });
      }
    },
  });
}

export function useUpdateRelayTransactionStatus() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (params: {
      transactionUnid: string;
      status: string;
    }): Promise<ResponsesRelayTransaction | null | undefined> => {
      if (!orgUnid) throw new Error('Organization not found');
      const request: any = { // TODO
        status: params.status,
      };
      const response = await apiClient.relayApi.updateRelayTransactionStatus(
        orgUnid,
        params.transactionUnid,
        '',
        request
      );
      return response.data?.data?.transaction;
    },
    onSuccess: (_data, params) => {
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: relayKeys.transaction(orgUnid, params.transactionUnid) });
        queryClient.invalidateQueries({ queryKey: [...relayKeys.all, 'transactions', orgUnid] });
        queryClient.invalidateQueries({ queryKey: relayKeys.status(orgUnid) });
      }
    },
  });
}
